import { IconActionCopy16, IconFileTrash16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackMessageMenuClick } from './tracking';
import {
  currentChatOpponentIdSelector,
  currentOpenChatSelector,
  isMasterSelector,
  userSelector,
} from '../../selectors';
import { openDeleteMessageModal } from '../../slices/deleteMessageSlice';
import { copyMessage } from '../../thunks/copyMessage';
import { TChatMessageMenuItem } from '../../types/chatMessageMenuItem';
import { EChatRole } from '../../types/userData';
import { useApplicationContext } from '../../utils/applicationContext';

export const useMenuItems = (chatId?: string, messageId?: string): TChatMessageMenuItem[] => {
  const {
    config,
    custom: { messagesMap },
  } = useApplicationContext();
  const dispatch = useDispatch();

  const user = useSelector(userSelector);
  const isMaster = useSelector(isMasterSelector);
  const openedChat = useSelector(currentOpenChatSelector);
  const opponentId = useSelector(currentChatOpponentIdSelector);

  const allowedTimeToDeleteMessage = config.get<number>('frontend-chats.allowedTimeToDeleteMessage') || 0;

  const canDelete = React.useMemo((): boolean => {
    if (!messageId || !openedChat) {
      return false;
    }

    const chatMessage = messagesMap.get(messageId);

    if (!chatMessage) {
      return false;
    }

    const {
      message: { message },
    } = chatMessage;

    const expired = Date.now() - message.created > allowedTimeToDeleteMessage;

    // Время для возможности удаления истекло
    if (expired) {
      return false;
    }

    // Сообщение отослано мной
    if (message.userId === user.userId) {
      return true;
    }

    const { users } = openedChat;
    const userChatRole = users.find(({ userId }) => userId === message.userId)?.chatRole;

    // Я - мастер и сообщение субагента
    if (isMaster && userChatRole === EChatRole.Opponent) {
      return true;
    }

    return false;
  }, [messageId, openedChat, messagesMap, allowedTimeToDeleteMessage, user.userId, isMaster]);

  return React.useMemo(() => {
    if (!chatId || !messageId) {
      return [];
    }

    const items = [
      {
        title: 'Скопировать',
        color: '#000000',
        handler(): void {
          trackMessageMenuClick({ chatId, label: 'Copy', opponentId });
          dispatch(copyMessage({ messageId }));
        },
        icon: <IconActionCopy16 color="icon-primary-default" />,
      },
    ];

    if (canDelete) {
      items.push({
        title: 'Удалить',
        color: '#DB1F36',
        handler(): void {
          if (!messageId) {
            return;
          }

          const chatMessage = messagesMap.get(messageId);

          if (!chatMessage) {
            return;
          }

          const {
            message: { message },
          } = chatMessage;

          const withPhoto = Boolean(message.files && (message.files.images.length || message.files.documents?.length));

          trackMessageMenuClick({ chatId, label: 'Delete_message', opponentId, withPhoto });
          dispatch(openDeleteMessageModal({ chatId, messageId, placeType: 'chat' }));
        },
        icon: <IconFileTrash16 color="current_color" />,
      });
    }

    return items;
  }, [canDelete, chatId, dispatch, messageId, messagesMap, opponentId]);
};
